/* import __COLOCATED_TEMPLATE__ from './leads.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import { HEIGHT_TYPES, WIDTH_TYPES } from 'embercom/models/reporting/custom/chart';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import { filterableProperties as defaultFilterableProperties } from 'embercom/lib/reporting/custom/filter-helpers';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import User from 'embercom/models/user';
import B64FilterParamEncoder from 'embercom/vendor/intercom/b64-filter-param-encoder';
import { getOutcomeMemoryFields } from 'embercom/lib/ai-automation/sdr-memory-fields';
import { QualificationRatesConfig } from 'embercom/components/reporting/bespoke/sdr-leads/qualification-rates/series-config';

type UserInstance = InstanceType<typeof User>;

type RoutingOutcome = {
  identifier: string;
  title: string;
  workflow: Record<string, unknown> | null;
  fin_instruction_text: string;
  predicates: Array<{ attribute: string; type: string; comparison: string; value: string }>;
  required_memory_fields: Array<{ identifier: string }>;
  optional_memory_fields: Array<{ identifier: string }>;
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
  general_instructions: Record<string, unknown> | null;
  integrations: Record<string, unknown> | null;
};

type UserSearchResult = {
  users: UserInstance[];
  totalCount: number;
  totalPages: number;
};

const DEFAULT_CHART_DEFINITION = {
  id: null,
  title: null,
  visualization_type: null,
  metric_property: null,
  metric_function: null,
  stacked: false,
  view_by: null,
  view_by_time_interval: null,
  view_by_display_limit: 10,
  view_by_display_other: false,
  width_type: null,
  height_type: null,
};

interface Signature {
  Args: {};
  Blocks: {
    default: [];
  };
  Element: null;
}

export default class Leads extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked finSdrConfig: SdrConfig | null = null;
  @tracked selectedOutcomeIdentifier: string | null = null;
  @tracked qualifiedLeads: UserInstance[] = [];
  @tracked totalLeadsCount = 0;
  @tracked currentPage = 1;
  @tracked selectedLeadUser: UserInstance | null = null;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
      let firstOutcome = this.sortedRoutingOutcomes[0];
      if (firstOutcome) {
        this.selectedOutcomeIdentifier = firstOutcome.identifier;
        taskFor(this.loadQualifiedLeads).perform();
      }
    } catch (error: unknown) {
      console.error('Failed to load SDR config:', error);
      this.finSdrConfig = null;
    }
  }

  @task({ restartable: true })
  *loadQualifiedLeads(): TaskGenerator<void> {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return;
    }
    try {
      let predicates = [
        {
          attribute: 'fin_qualification_status',
          type: 'string',
          comparison: 'eq',
          value: outcome.title,
        },
      ];
      let result = (yield User.search(this.appService.app, predicates, {
        page: this.currentPage,
        per_page: 25,
      })) as UserSearchResult;
      if (this.currentPage === 1) {
        this.qualifiedLeads = [...result.users];
      } else {
        this.qualifiedLeads = [...this.qualifiedLeads, ...result.users];
      }
      this.totalLeadsCount = result.totalCount;
    } catch (e: unknown) {
      console.error('Failed to load qualified leads:', e);
      this.qualifiedLeads = [];
      this.totalLeadsCount = 0;
    }
  }

  get sortedRoutingOutcomes(): RoutingOutcome[] {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];

    let priority = (outcome: RoutingOutcome) => {
      let identifier = outcome.identifier.toLowerCase();
      if (identifier === 'disqualified') {
        return 2;
      }
      if (identifier === 'escalate_to_support') {
        return 3;
      }
      return 1;
    };

    return [...outcomes].sort((a, b) => priority(a) - priority(b));
  }

  get isDrawerOpen(): boolean {
    return this.selectedLeadUser !== null;
  }

  get drawerUser(): UserInstance {
    return this.selectedLeadUser!;
  }

  @action
  selectLead(user: UserInstance) {
    if (this.selectedLeadUser === user) {
      this.selectedLeadUser = null;
    } else {
      this.selectedLeadUser = user;
    }
  }

  @action
  closeDrawer() {
    this.selectedLeadUser = null;
  }

  @action
  stopRowClickPropagation(event: Event) {
    event.stopPropagation();
  }

  @action
  selectOutcome(identifier: string) {
    this.selectedOutcomeIdentifier = identifier;
    this.selectedLeadUser = null;
    this.currentPage = 1;
    this.qualifiedLeads = [];
    this.totalLeadsCount = 0;
    taskFor(this.loadQualifiedLeads).perform();
  }

  get selectedOutcome(): RoutingOutcome | undefined {
    return this.sortedRoutingOutcomes.find((o) => o.identifier === this.selectedOutcomeIdentifier);
  }

  get isLoadingLeads(): boolean {
    return taskFor(this.loadQualifiedLeads).isRunning;
  }

  get dynamicColumns() {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return [];
    }
    return getOutcomeMemoryFields(outcome);
  }

  get tableColumns(): Array<{ label: string; valuePath: string }> {
    let columns = [
      {
        label: this.intl.t('ai-agent.analyze.leads.table.contact-name'),
        valuePath: 'contactName',
      },
    ];
    for (let col of this.dynamicColumns) {
      columns.push({ label: col.label, valuePath: col.key });
    }
    columns.push({
      label: this.intl.t('ai-agent.analyze.leads.table.conversation'),
      valuePath: 'conversation',
    });
    return columns;
  }

  get leadsTableData(): Array<{ user: UserInstance; conversationUrl: string }> {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return [];
    }
    return this.qualifiedLeads.map((user: UserInstance) => ({
      user,
      conversationUrl: this.buildConversationSearchUrl(user, outcome),
    }));
  }

  get hasMorePages(): boolean {
    return this.qualifiedLeads.length < this.totalLeadsCount;
  }

  @action
  loadNextPage() {
    this.currentPage += 1;
    taskFor(this.loadQualifiedLeads).perform();
  }

  private buildConversationSearchUrl(user: UserInstance, outcome: RoutingOutcome): string {
    let predicates = {
      predicates: [
        {
          identifier: 'user',
          attribute: 'user_ids',
          comparison: 'eq',
          type: 'id',
          value: user.id.toString(),
        },
        {
          identifier: 'conversation-attribute',
          type: 'and',
          predicates: [
            {
              attribute: 'conversation.fin_sdr_participated',
              comparison: 'true',
              type: 'boolean',
              value: 'true',
            },
            {
              attribute: 'conversation.fin_sdr_routing_outcome',
              comparison: 'eq',
              type: 'string',
              value: outcome.identifier,
            },
          ],
        },
      ],
    };
    let encoded = B64FilterParamEncoder.b64Encode(JSON.stringify(predicates));
    let appId = this.appService.app.id;
    return `/a/inbox/${appId}/inbox/search?filters=${encoded}`;
  }

  get qualificationOverviewCharts() {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return [];
    }
    let outcomeFilter = {
      type: 'and',
      filters: [
        {
          type: 'category',
          data: {
            attribute: 'conversation.fin_sdr.routing_outcome_title',
            property: 'fin_sdr.routing_outcome.title',
            values: [outcome.title],
          },
        },
      ],
    };

    return [
      {
        ...DEFAULT_CHART_DEFINITION,
        id: `sdr_qualification_prospects_${outcome.identifier}`,
        title: this.intl.t('ai-agent.analyze.leads.prospects-count', {
          outcome: outcome.title,
        }),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: { show_time_comparison: true },
        width_type: WIDTH_TYPES.THREE_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'conversation.fin_sdr.participated.count',
            show_data_labels: true,
            filters: outcomeFilter,
          },
        ],
      },
      {
        ...DEFAULT_CHART_DEFINITION,
        id: `sdr_qualification_rate_${outcome.identifier}`,
        title: this.intl.t('ai-agent.analyze.leads.outcome-rate', {
          outcome: outcome.title,
        }),
        visualization_type: VISUALIZATION_TYPES.BESPOKE,
        component_name: 'sdr-leads/qualification-rates',
        width_type: WIDTH_TYPES.THREE_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: QualificationRatesConfig.getChartSeries(this.intl, outcome),
      },
    ];
  }

  get serializedReport() {
    return {
      id: 'fin-sdr-qualification',
      title: null,
      description: null,
      charts: [],
      filters: {},
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get filterableProperties() {
    return defaultFilterableProperties().concat(
      'teammate',
      'team',
      'conversation.replied.workflow_ids',
    );
  }

  get shouldShowReportFilters() {
    return true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::Leads': typeof Leads;
    'ai-automation/analyze/leads': typeof Leads;
  }
}
